import { AfterViewInit, Component, effect, EventEmitter, Injector, Input, OnChanges, OnDestroy, OnInit, Output, runInInjectionContext, signal, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatSelectSearchComponent, NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { ReplaySubject, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { User } from '../../../models/user.model';
import { UserService } from '../../../services/user/user.service';
import { PermissionMap, PermissionService } from '../../../services/settings/permission.service';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatTooltip } from '@angular/material/tooltip';
import { AsyncPipe } from '@angular/common';

export interface UserOption {
  user: User;
  selected: boolean;
}

@Component({
    selector: 'app-user-search-by-permission',
    templateUrl: './user-search-by-permission.component.html',
    styleUrls: ['./user-search-by-permission.component.scss'],
    imports: [MatFormField, MatLabel, MatSelect, FormsModule, ReactiveFormsModule, MatTooltip, MatSelectTrigger, MatOption, NgxMatSelectSearchModule, MatError, AsyncPipe]
})
export class UserSearchByPermissionComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {

  private _disabled = signal(false);
  
  @Input() set disabled(value: boolean) {
    this._disabled.set(value);
  }

  @Input() form: FormGroup;
  @Input() color = 'primary';
  @Input() appearance = 'outline';
  @Input() floatLabel = 'always';
  @Input() multiple = true;
  @Input() controlName = 'user-search';
  @Input() label = 'Search User';
  @Input() placeholder = '';
  @Input() searchLabel = 'Search';
  @Input() toolTipPosition = 'above';
  @Input() required = false;
  @Input() queryOptions = false;
  @Input() unassignedEnabled = true;
  @Input() restrictToCurrentUser: boolean | null = null;

  userOptions: UserOption[] = [];
  filteredOptions: ReplaySubject<UserOption[]> = new ReplaySubject<UserOption[]>(1);
  control = new FormControl<UserOption[] | UserOption | null>(null);
  filterControl = new FormControl<string>('');
  allToggled = false;
  indeterminateToggle = false;
  displayOptionLengthFeatureSubtract = 0;

  unassignedOption: UserOption = {user: {user_id: '-1', user_guid: '-1', given_name: 'Unassigned', family_name: ''} as User, selected: false};

  currentUser: User;
  viewEditPermActive = false;
  assignPermActive = false;
  usersByPermission: User[] = [];
  subs: Subscription[] = [];

  protected _onDestroy = new Subject<void>();

  @Output() ready = new EventEmitter();
  @Output() valueChanged = new EventEmitter();

  @ViewChild(MatSelect) select: MatSelect;
  @ViewChild(MatSelectSearchComponent) searchFilter: MatSelectSearchComponent;

  constructor(
    private userService: UserService,
    private permissionService: PermissionService,
    private injector: Injector) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes['unassignedEnabled']) {
      this.displayOptionLengthFeatureSubtract = this.unassignedEnabled ? 1 : 0;
    }

    if((changes['restrictToCurrentUser'] || changes['unassignedEnabled']) && this.usersByPermission.length > 0) {
      this.refreshOptionsFromCurrentState();
    }
  }

  ngOnInit(): void {
    this.displayOptionLengthFeatureSubtract = this.unassignedEnabled ? 1 : 0;
    
    if(this.required) {
      this.control.setValidators([Validators.required]);
    }

    this.form?.addControl(this.controlName, this.control);

    runInInjectionContext(this.injector, () => {
      effect(() => {
        if(this._disabled()) {
          this.control?.disable({emitEvent: false});
        } else {
          this.control?.enable({emitEvent: false});
        }
      })});

    this.subs.push(this.userService.currentUser$.subscribe(result => {
      this.currentUser = result;

      if(this.usersByPermission.length > 0 && this.isRestrictedToCurrentUser()) {
        this.refreshOptionsFromCurrentState();
      }
    }));

    this.subs.push(this.permissionService.activeUserPermissions.subscribe(result => {
      this.viewEditPermActive = result.find(p => p.PermissionID == PermissionMap.ViewAndEditAllDxEvents) ? true : false;
      this.assignPermActive = result.find(p => p.PermissionID == PermissionMap.AssignOpportunities) ? true : false;

      if(this.restrictToCurrentUser === null && this.usersByPermission.length > 0) {
        this.refreshOptionsFromCurrentState();
      }
    }));

    const usersByPermission = this.userService.usersByPermission.get(PermissionMap.AcceptsOpportunityAssignment);
    if(usersByPermission) {
      this.subs.push(usersByPermission.subscribe(result => {
        if(!result) {
          if(this.queryOptions) {
            this.getOptionData();
          }
          return;
        }
        this.usersByPermission = result;
        this.refreshOptionsFromCurrentState();
        this.ready.emit(this.controlName);
      }));
    }

    this.filterControl.valueChanges
    .pipe(takeUntil(this._onDestroy))
    .subscribe(() => {
      this.filterMulti();

      if(this.multiple) {
        this.setAllToggleState();
      }
    });

    if(this.multiple) {
      this.control.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.setAllToggleState();
      });
    }
  }

  ngAfterViewInit(): void {
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  getOptionData() {
    this.userService.getUserListByPermission(PermissionMap.AcceptsOpportunityAssignment);
  }

  setSelectedValues(values: User[] | null) {

    if(!values) {
      this.control?.reset();
      this.control?.setValue(this.multiple ? [] : null);
      return;
    }

    if(values.length == 0) {
      if(this.multiple) {
        this.allOptionClicked(true);
      } else {
        this.control?.setValue(null, {emitEvent: false});
      }
      return;
    }

    // Build UserOption objects for the given users and set the control value directly
    const userOptionsToSelect: UserOption[] = [];

    this.userOptions.forEach(uo => {
      uo.selected = false;
    });

    this.unassignedOption.selected = false;
    
    values.forEach(u => {
      // Find or create the UserOption for this user
      let userOption = this.userOptions.find(uo => uo.user.user_id === u.user_id);
      if(!userOption) {
        userOption = {user: u, selected: true};
      } else {
        userOption.selected = true;
      }
      userOptionsToSelect.push(userOption);
    });

    // Set the FormControl value directly, without relying on this.select.options
    this.control.setValue(this.multiple ? userOptionsToSelect : (userOptionsToSelect[0] ?? null), {emitEvent: false});
    
    // Fallback: if select.options is available, also select via MatOption API for consistency
    if(this.select?.options) {
      userOptionsToSelect.forEach(o => {
        const found = this.select.options.find((i, index) => {
          if(index == 0) {
            return false;
          }
          return i.value.user.user_id == o.user.user_id;
        });

        if(found) {
          // Use non-emitting select to avoid duplicate selectionChange/valueChanges events
          found.select(false);
        }
      });
    }

    if(this.multiple) {
      this.setAllToggleState();
    }
  }

  getValues() {

    if(this.allToggled && this.multiple && !this.isRestrictedToCurrentUser()) {
      return [];
    }

    return this.getSelectedOptions().map(i => i.user);
  }

  getSingleModeValue() {
    const value = this.control?.value;
    if(!value) {
      return null;
    }

    return Array.isArray(value) ? (value[0] ?? null) : value;
  }

  displayTooltip() {
    let result = '';
    const users = this.getSelectedOptions();

    if(users.length == 0) {
      return result;
    }

    users.forEach((u, index) => {
      if(!u?.user) {
        return;
      }

      result += (u.user.given_name + ' ' + u.user.family_name).trim();

      if(index < users.length - 1) {
        result += ', ';
      }
    })
    return result;
  }

  allOptionClicked(isChecked: boolean) {
    this.userOptions.forEach(o => {
      o.selected = isChecked;
    });

    if(this.unassignedEnabled) {
      this.unassignedOption.selected = isChecked;
    }

    const selectedValues = isChecked
      ? (this.unassignedEnabled ? [this.unassignedOption, ...this.userOptions] : [...this.userOptions])
      : [];

    this.control?.setValue(selectedValues, {emitEvent: false});

    if(this.multiple) {
      this.setAllToggleState();
    }

    if(!this.select?.options) {
      return;
    }

    this.select.options.forEach((o, index) => {
      if(index == 0) {
        return;
      }

      isChecked ? o.select(false) : o.deselect(false);
    });

  }

  onToggleAll(isChecked: boolean) {
    this.allOptionClicked(isChecked);
    // allOptionClicked sets the value without emitting; notify the parent form so its validity (e.g. required) is re-evaluated
    this.control.updateValueAndValidity();
  }

  toggleSelection(option: MatOption, userOption: UserOption) {
    userOption.selected = option.selected;
  }

  checkAllOptions() {
    let result = true;
    this.userOptions.forEach(o => {
      if(!o.selected) {
        result = false;
      }
    });

    return result;
  }

  onSelectionChange(selected: UserOption[]) {
    const values = selected.map(i => i.user);
    this.valueChanged.emit(values);
  }

  getSelectCount() {
    return this.getSelectedOptions().length;
  }

  filterMulti() {
    if (!this.userOptions) {
      return;
    }

    let search = this.filterControl?.value;

    if (!search) {
      this.filteredOptions.next(this.userOptions.slice());
      return;
    } else {
      search = search.toLowerCase();
    }

    this.filteredOptions.next(
      this.userOptions.filter(o => (o.user.given_name.toLowerCase() + ' ' + o.user.family_name).indexOf(search) > -1)
    );
  }

  setAllToggleState() {
    let filteredLength = 0;
    const listSize = this.unassignedEnabled ? this.userOptions.length + 1 : this.userOptions.length;
    const selectedOptions = this.getSelectedOptions();
    if(selectedOptions.length > 0) {
      this.userOptions.forEach(o => {
        if(selectedOptions.indexOf(o) > -1) {
          filteredLength++;
        }
      });

      if(this.unassignedEnabled && selectedOptions.indexOf(this.unassignedOption) > -1) {
        filteredLength++;
      }

      this.indeterminateToggle = filteredLength > 0 && filteredLength < listSize;
      this.allToggled = filteredLength > 0 && filteredLength === listSize;
    } else {
      this.indeterminateToggle = false;
      this.allToggled = false;
    }
  }

  private getSelectedOptions(): UserOption[] {
    const value = this.control?.value;

    if(!value) {
      return [];
    }

    return Array.isArray(value) ? value : [value];
  }

  private getCurrentUserID() {
    return this.currentUser?.user_guid || this.currentUser?.uid || this.currentUser?.user_id;
  }

  private isRestrictedToCurrentUser() {
    if(this.restrictToCurrentUser !== null && this.restrictToCurrentUser !== undefined) {
      return this.restrictToCurrentUser;
    }

    return !(this.viewEditPermActive || this.assignPermActive);
  }

  private refreshOptionsFromCurrentState() {
    this.userOptions = [];
    this.setSelectedValues(null);

    if(!this.isRestrictedToCurrentUser()) {
      this.usersByPermission.forEach(u => {
        this.userOptions.push({user: u, selected: false});
      });
    } else {
      const currentUserID = this.getCurrentUserID();
      const foundUser = this.usersByPermission.find(u => u.user_guid == currentUserID);

      if(foundUser) {
        this.userOptions.push({user: foundUser, selected: true});
        this.setSelectedValues([foundUser]);
      }
    }

    this.filteredOptions.next(this.userOptions);

    if(this.multiple) {
      this.setAllToggleState();
    }
  }

}
